package br.com.vvrefeicoes.portal.feature.audit

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.AuditEntry
import br.com.vvrefeicoes.portal.domain.repository.AuditRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.number

enum class AuditDateFilter { WEEK, MONTH, ALL, CUSTOM }

class AuditController(
    private val repository: AuditRepository,
    private val scope: CoroutineScope,
    private val dateTimeProvider: DateTimeProvider = SystemDateTimeProvider(),
) {
    var loading by mutableStateOf(true)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var entries by mutableStateOf<List<AuditEntry>>(emptyList())
        private set

    var query by mutableStateOf("")
        private set

    var dateFilter by mutableStateOf(AuditDateFilter.WEEK)
        private set

    var customStart by mutableStateOf("")
        private set

    var customEnd by mutableStateOf("")
        private set

    var page by mutableStateOf(0)
        private set

    var customOpen by mutableStateOf(false)
        private set

    var detailsEntry by mutableStateOf<AuditEntry?>(null)
        private set

    var detailsLoading by mutableStateOf(false)
        private set

    var detailsError by mutableStateOf<String?>(null)
        private set

    var customError by mutableStateOf<String?>(null)
        private set

    val pageSize = 20

    val pageCount: Int
        get() = maxOf(1, totalPages)

    val paged: List<AuditEntry>
        get() = entries

    private var totalPages by mutableStateOf(1)
    private var loadJob: Job? = null
    private var detailJob: Job? = null

    fun init() {
        if (!loading) return
        requestPage()
    }

    fun retry() {
        errorMessage = null
        requestPage()
    }

    fun updateQuery(value: String) {
        query = value
        page = 0
        requestPage()
    }

    fun updateDateFilter(value: AuditDateFilter) {
        if (value == AuditDateFilter.CUSTOM) {
            customOpen = true
            return
        }
        dateFilter = value
        page = 0
        requestPage()
    }

    fun dismissCustom() {
        customOpen = false
        customError = null
    }

    fun applyCustom(
        start: String,
        end: String,
    ) {
        val startIso = toIso(start)
        val endIso = toIso(end)
        if (startIso == null || endIso == null || startIso > endIso) {
            customError = "Informe um período válido."
            return
        }
        customStart = start
        customEnd = end
        dateFilter = AuditDateFilter.CUSTOM
        customOpen = false
        page = 0
        requestPage()
    }

    fun goToPage(value: Int) {
        val target = value.coerceIn(0, pageCount - 1)
        if (target == page) return
        page = target
        requestPage()
    }

    fun openDetails(entry: AuditEntry) {
        detailsEntry = entry
        detailsLoading = true
        detailsError = null
        detailJob?.cancel()
        detailJob =
            scope.launch {
                try {
                    detailsEntry = repository.getEntry(entry.id)
                } catch (exception: CancellationException) {
                    throw exception
                } catch (throwable: Throwable) {
                    detailsError = throwable.message ?: "Não foi possível carregar os detalhes."
                } finally {
                    detailsLoading = false
                }
            }
    }

    fun retryDetails() {
        detailsEntry?.let(::openDetails)
    }

    fun dismissDetails() {
        detailJob?.cancel()
        detailsEntry = null
        detailsError = null
        detailsLoading = false
    }

    private val today = dateTimeProvider.today()

    private val weekStart = today.minus(7, DateTimeUnit.DAY).toString()

    private val currentMonth = "${today.year}-${today.month.number.toString().padStart(2, '0')}"

    private fun requestPage() {
        loadJob?.cancel()
        loadJob = scope.launch { loadPage() }
    }

    private suspend fun loadPage() {
        loading = true
        errorMessage = null
        entries = emptyList()
        totalPages = 1
        try {
            val result =
                repository.loadEntries(
                    page = page,
                    size = pageSize,
                    from = dateFilter.fromQuery(customStart, weekStart, currentMonth),
                    to = dateFilter.toQuery(customEnd),
                    query = query.trim().takeIf { it.isNotEmpty() },
                )
            entries = result.entries
            totalPages = result.totalPages
        } catch (exception: CancellationException) {
            throw exception
        } catch (throwable: Throwable) {
            errorMessage = throwable.message ?: "Não foi possível carregar a auditoria."
        } finally {
            loading = false
        }
    }
}

private fun toIso(value: String): String? {
    val parts = value.trim().split("/")
    if (parts.size != 3) return null
    val day = parts[0].padStart(2, '0')
    val month = parts[1].padStart(2, '0')
    val year = parts[2]
    if (day.length != 2 || month.length != 2 || year.length != 4) return null
    if (day.toIntOrNull() == null || month.toIntOrNull() == null || year.toIntOrNull() == null) return null
    return runCatching {
        LocalDate(year.toInt(), month.toInt(), day.toInt()).toString()
    }.getOrNull()
}

private fun AuditDateFilter.fromQuery(
    customStart: String,
    weekStart: String,
    currentMonth: String,
): String? =
    when (this) {
        AuditDateFilter.WEEK -> "${weekStart}T00:00:00Z"
        AuditDateFilter.MONTH -> "$currentMonth-01T00:00:00Z"
        AuditDateFilter.ALL -> null
        AuditDateFilter.CUSTOM -> toIso(customStart)?.let { "${it}T00:00:00Z" }
    }

private fun AuditDateFilter.toQuery(customEnd: String): String? =
    when (this) {
        AuditDateFilter.WEEK, AuditDateFilter.MONTH, AuditDateFilter.ALL -> null
        AuditDateFilter.CUSTOM -> toIso(customEnd)?.let { "${it}T23:59:59Z" }
    }

@Composable
fun rememberAuditController(): AuditController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember { AuditController(appGraph.auditRepository, scope, appGraph.dateTimeProvider) }
}
